import { useQuery } from '@tanstack/react-query'
import { Link, NavLink, Outlet, useLocation, useSearchParams } from 'react-router-dom'
import { cn } from '../../lib/cn.js'
import { useJobOverlay } from '../../lib/job-overlay.js'
import { useIsTablet } from '../../lib/media.js'
import { useConnectionState } from '../../lib/realtime.js'
import { useSession } from '../../lib/session.js'
import { useTRPC } from '../../lib/trpc.js'
import { Icon, type IconName } from '../Icon.js'
import { JobOverlay } from '../JobOverlay.js'
import { NAV, activeNavItem, navItemIsActive, useUnreadCount } from '../nav.js'
import { Avatar } from '../ui.js'
import { FieldJob, FieldJobLayer } from './FieldJob.js'
import { FieldThread } from './FieldInbox.js'
import { TeamMap } from './FieldTeam.js'
import { todayWindow } from './kit.js'

/**
 * The field app: what a phone, or any touch-first screen, gets instead of the
 * office rail. The EcoPest iOS mockup. Decision 93.
 *
 * A technician has Today, Jobs and Me; the office adds Team and Inbox. Every
 * other screen is still reachable, as a list inside Me, for whoever has the
 * permission — the field app is built for the technician and does not take
 * anything away from the office.
 *
 * On a phone the tabs are a glass bar along the bottom and a job slides up
 * over whatever screen you are on. On an iPad it is a sidebar, a list and a
 * detail pane, and a job opened from Today or Jobs fills the right-hand side
 * rather than covering the list it came from.
 */

interface FieldTab {
  to: string
  label: string
  icon: IconName
}

/** The routes an iPad draws as a list beside a detail pane. */
const SPLIT_ROUTES = new Set(['/', '/jobs', '/team', '/inbox', '/me'])

function useFieldTabs(): FieldTab[] {
  const { can } = useSession()
  return [
    { to: '/', label: 'Today', icon: 'today' as const },
    ...(can('JOB_VIEW') ? [{ to: '/jobs', label: 'Jobs', icon: 'jobs' as const }] : []),
    ...(can('SCHEDULE_VIEW_ALL') ? [{ to: '/team', label: 'Team', icon: 'people' as const }] : []),
    ...(can('INBOX_VIEW') ? [{ to: '/inbox', label: 'Inbox', icon: 'inbox' as const }] : []),
    { to: '/me', label: 'Me', icon: 'clients' as const },
  ]
}

/** Whichever tab the screen belongs to. Anything reached from Me lights Me. */
function currentTab(tabs: FieldTab[], pathname: string): FieldTab | undefined {
  return tabs.find((tab) => navItemIsActive(tab, pathname)) ?? tabs.at(-1)
}

/** Bookings today with nobody on them. The Team tab's badge. */
function useUnassignedToday(): number {
  const trpc = useTRPC()
  const { can } = useSession()
  const { data } = useQuery({
    ...trpc.schedule.board.queryOptions(todayWindow()),
    enabled: can('SCHEDULE_VIEW_ALL'),
  })
  return (data ?? []).filter(
    (visit) =>
      visit.assignments.length === 0 && visit.status !== 'CANCELLED' && visit.status !== 'NO_SHOW',
  ).length
}

function useBadges(): Record<string, number> {
  return { '/inbox': useUnreadCount(), '/team': useUnassignedToday() }
}

export function FieldShell() {
  const tablet = useIsTablet()
  const tabs = useFieldTabs()
  return tablet ? <TabletShell tabs={tabs} /> : <PhoneShell tabs={tabs} />
}

/* -------------------------------------------------------------------------- */
/* Phone                                                                      */
/* -------------------------------------------------------------------------- */

function PhoneShell({ tabs }: { tabs: FieldTab[] }) {
  const { pathname } = useLocation()
  const onTab = tabs.some((tab) => tab.to === pathname)

  return (
    <div className="relative flex h-full flex-col bg-ink-100">
      <main
        className={cn(
          'min-h-0 flex-1 overflow-y-auto pb-[calc(4.5rem+var(--safe-bottom))]',
          onTab && 'pt-[var(--safe-top)]',
        )}
      >
        {/* Eco's page fills the screen and has its own header and way back. */}
        {onTab || pathname === '/eco' ? null : <BackBar pathname={pathname} />}
        <Outlet />
      </main>
      <TabBar tabs={tabs} />
      <FieldJobLayer inline={false} />
      <JobOverlay field />
    </div>
  )
}

/**
 * A screen reached from Me — the board, a client, Settings — has no tab of its
 * own, so it gets a way back to the list it came from.
 */
function BackBar({ pathname }: { pathname: string }) {
  const title = pathname.startsWith('/people/')
    ? 'People'
    : pathname.startsWith('/help')
      ? 'Help'
      : pathname === '/worklist'
      ? 'Worklist'
      : (activeNavItem(NAV, pathname)?.label ?? '')

  return (
    <div className="sticky top-0 z-20 flex items-center gap-2 bg-ink-100/90 px-2 pt-[calc(var(--safe-top)+6px)] pb-2 backdrop-blur-xl shadow-[inset_0_-1px_0_var(--color-ink-200)]">
      <Link
        to="/me"
        className="flex h-9 items-center gap-0.5 rounded-md px-1.5 text-[16px] font-medium text-brand-700"
      >
        <Icon name="back" className="size-5" />
        Me
      </Link>
      <span className="min-w-0 flex-1 truncate pr-12 text-center text-[16px] font-semibold text-ink-900">
        {title}
      </span>
    </div>
  )
}

function TabBar({ tabs }: { tabs: FieldTab[] }) {
  const { pathname } = useLocation()
  const current = currentTab(tabs, pathname)
  const badges = useBadges()

  return (
    <nav
      aria-label="Main"
      className="fixed inset-x-0 bottom-0 z-20 flex bg-white/88 pb-[var(--safe-bottom)] shadow-[inset_0_1px_0_var(--color-ink-200)] backdrop-blur-xl"
    >
      {tabs.map((tab) => {
        const active = tab === current
        const badge = badges[tab.to] ?? 0
        return (
          <NavLink
            key={tab.to}
            to={tab.to}
            aria-current={active ? 'page' : undefined}
            className={cn(
              'relative flex h-[52px] flex-1 flex-col items-center justify-center gap-0.5 text-[10px] font-medium',
              active ? 'text-brand-600' : 'text-ink-500',
            )}
          >
            <Icon name={tab.icon} className="size-6" />
            {tab.label}
            {badge > 0 ? (
              <span
                aria-label={`${badge} ${tab.to === '/inbox' ? 'unread' : 'need a tech'}`}
                className="absolute top-1.5 left-1/2 ml-2 size-[9px] rounded-full bg-red-600 ring-2 ring-white"
              />
            ) : null}
          </NavLink>
        )
      })}
    </nav>
  )
}

/* -------------------------------------------------------------------------- */
/* iPad                                                                       */
/* -------------------------------------------------------------------------- */

function TabletShell({ tabs }: { tabs: FieldTab[] }) {
  const { pathname } = useLocation()
  const split = SPLIT_ROUTES.has(pathname)
  const jobInPane = split && (pathname === '/' || pathname === '/jobs')

  return (
    <div className="flex h-full bg-ink-100">
      <Sidebar tabs={tabs} />

      {split ? (
        <>
          <main className="w-[380px] shrink-0 overflow-y-auto pt-[calc(var(--safe-top)+8px)] shadow-[inset_-1px_0_0_var(--color-ink-200)]">
            <Outlet />
          </main>
          <section
            aria-label="Detail"
            className="relative flex min-w-0 flex-1 flex-col overflow-hidden pt-[var(--safe-top)]"
          >
            <DetailPane pathname={pathname} />
          </section>
        </>
      ) : (
        <main className="min-w-0 flex-1 overflow-y-auto pt-[var(--safe-top)]">
          <Outlet />
        </main>
      )}

      <FieldJobLayer inline={jobInPane} />
      <JobOverlay field />
    </div>
  )
}

function Sidebar({ tabs }: { tabs: FieldTab[] }) {
  const { user } = useSession()
  const { pathname } = useLocation()
  const connection = useConnectionState()
  const current = currentTab(tabs, pathname)
  const badges = useBadges()

  return (
    <aside className="flex w-[220px] shrink-0 flex-col bg-[#e9efeb] px-3 pt-[calc(var(--safe-top)+18px)] pb-[calc(var(--safe-bottom)+14px)] shadow-[inset_-1px_0_0_#dde4df]">
      <img
        src="/brand/ecopest-horizontal-colour-on-light.svg"
        alt="EcoPest"
        className="mb-6 ml-2 h-8 w-auto self-start"
      />

      <nav aria-label="Main" className="flex flex-col gap-0.5">
        {tabs.map((tab) => {
          const active = tab === current
          const badge = badges[tab.to] ?? 0
          return (
            <NavLink
              key={tab.to}
              to={tab.to}
              aria-current={active ? 'page' : undefined}
              className={cn(
                'flex h-[42px] items-center gap-3 rounded-md px-3 text-[15px] font-medium transition-colors',
                active ? 'bg-ink-900 text-white' : 'text-ink-700 active:bg-white/70',
              )}
            >
              <Icon name={tab.icon} className="size-5" />
              <span className="flex-1">{tab.label}</span>
              {badge > 0 ? (
                <span
                  className={cn(
                    'min-w-5 rounded-sm px-1.5 text-center text-[11px] leading-5 font-bold',
                    active ? 'bg-white text-ink-900' : 'bg-red-600 text-white',
                  )}
                >
                  {badge}
                </span>
              ) : null}
            </NavLink>
          )
        })}
      </nav>

      <div className="mt-auto flex items-center gap-2.5 px-2 pt-3">
        <Avatar
          name={user?.name ?? '?'}
          colour={user?.colour}
          className={cn('size-8 text-[11px]', connection !== 'open' && 'ring-2 ring-red-600')}
        />
        <div className="min-w-0">
          <p className="truncate text-sm font-semibold text-ink-900">{user?.name}</p>
          <p className="truncate text-xs text-ink-500">
            {connection === 'open'
              ? (user?.roleName ?? '')
              : connection === 'connecting'
                ? 'Reconnecting…'
                : 'Offline'}
          </p>
        </div>
      </div>
    </aside>
  )
}

/**
 * The right-hand side of the iPad: the job on Today and Jobs, the message on
 * Inbox, the map on Team, and the logo when there is nothing to show.
 */
function DetailPane({ pathname }: { pathname: string }) {
  const { jobId } = useJobOverlay()
  const [params] = useSearchParams()

  if (pathname === '/team') return <TeamMap large />

  if (pathname === '/inbox') {
    const selected = params.get('selected')
    return selected ? <FieldThread key={selected} id={selected} /> : <Blank />
  }

  if ((pathname === '/' || pathname === '/jobs') && jobId) {
    return <FieldJob key={jobId} jobId={jobId} />
  }

  return <Blank />
}

function Blank() {
  return (
    <div className="grid flex-1 place-items-center">
      <img
        src="/brand/ecopest-mark-colour-on-light.svg"
        alt=""
        aria-hidden="true"
        className="h-20 w-auto opacity-20 grayscale"
      />
    </div>
  )
}
